import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Spin,
  Select,
  DatePicker,
  type DatePickerProps,
  Modal,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { type AppDispatch } from '../../../redux/store';
import { updateDepositAction, getDepositReceiptAction } from '../store/depositActions';
import { type Deposit, type DepositReceiptData } from '../store/depositTypes';
import { type Dayjs } from 'dayjs';
import DepositReceiptPrintable from '../DepositReceiptPrintable';

export default function DepositIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId') ?? '';

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const [branch, setBranch] = useState<any>('');

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      setBranchOptions(branchOptionTemp);

      // Auto-populate branch from customerId if present
      if (customerId !== '') {
        const branchCode = customerId.substring(0, 3);
        const matchingBranch = parsedBranches.find(
          (b: { code: string; id: string }) => b.code === branchCode
        );
        if (matchingBranch != null) {
          setBranch(matchingBranch.id);
        } else {
          setBranch(parsedBranches[0].id);
        }
      } else {
        setBranch(parsedBranches[0].id);
      }
    }
  }, [customerId]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // const { deposit }: any = useSelector<any>((state) => state?.deposit);

  // const [onlineAmount, setOnlineAmount] = useState<number>(0);
  // const [offlineAmount, setOfflineAmount] = useState<number>(0);
  // const [investedBy, setInvestedBy] = useState<string>('');

  const [form] = Form.useForm();
  const [interest, setInterest] = useState('15');
  const [amount, setAmount] = useState<number>(0);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<DepositReceiptData | null>(null);

  form.setFieldsValue({ branch, interest: '15' });

  // const getDepositValues = async (): Promise<void> => {
  //   await getDepositAction(dispatch, { branch: 'Krr' });
  // };

  // useEffect(() => {
  //   void getDepositValues();
  // }, []);

  const onFinish = async (values: Deposit) => {
    setIsLoading(true);
    const response = await updateDepositAction(dispatch, {
      ...values,
      branch,
      interest,
      amount,
      customer: customerId !== '' ? customerId : undefined,
    });

    if (response !== false && response?.depositNumber !== undefined) {
      // Fetch receipt data using the depositNumber
      const receiptResponse = await getDepositReceiptAction(dispatch, String(response.depositNumber));
      setIsLoading(false);
      if (receiptResponse?.receipt !== undefined) {
        setReceiptData(receiptResponse.receipt);
        setReceiptModalOpen(true);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleReceiptModalClose = () => {
    setReceiptModalOpen(false);
    setReceiptData(null);

    // If customerId is present, redirect to customer page with deposit tab
    if (customerId !== '') {
      navigate(`/customer?customerId=${customerId}&tab=1`);
    } else {
      // Reset form fields if not redirecting
      form.resetFields();

      // Reset state variables
      setDatePickerDate(null);
      setInterest('15');
      setAmount(0);

      // Reset branch to first option if no customerId
      const defaultBranch =
        branchOptions.length > 0 ? branchOptions[0].value : '';
      setBranch(defaultBranch);

      // Set form field values to defaults
      form.setFieldsValue({
        interest: '15',
        branch: defaultBranch,
      });
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(null);

  const onChangeDate: DatePickerProps['onChange'] = (
    date: Dayjs | null,
    dateString: string
  ): void => {
    form.setFieldsValue({ investedDate: dateString });
    // setDob(dateString);
    setDatePickerDate(date);
  };

  return (
    <div className="deposit__page">
      <h1>Deposit Form</h1>
      <Form
        form={form}
        name="deposit_form"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Spin spinning={isLoading} fullscreen />
        <div className="deposit__form">
          {customerId !== '' && (
            <Form.Item name="customer">
              <div>
                <h4>Customer ID</h4>
                <Input
                  disabled
                  value={customerId}
                  className="deposit__input"
                  placeholder="Customer ID"
                />
              </div>
            </Form.Item>
          )}
          <Form.Item
            name="branch"
            rules={[{ required: true, message: 'Please select branch!' }]}
          >
            <div className="customers__input__container">
              <h4>Branch</h4>
              <Select
                value={branch}
                onChange={(value: string): void => {
                  form.setFieldsValue({ branch: value });
                  setBranch(value);
                }}
                style={{ width: 311 }}
                placeholder="Select branch"
                options={branchOptions}
                className="deposit__input"
              />
            </div>
          </Form.Item>

          <Form.Item
            name="interest"
            rules={[{ required: true, message: 'Please select interest' }]}
          >
            <div>
              <h4>Interest</h4>
              <Select
                style={{ width: 311 }}
                defaultValue={interest}
                placeholder="Select interest"
                options={[
                  { value: '12', label: '12%' },
                  { value: '15', label: '15%' },
                  { value: '18', label: '18%' },
                  { value: '19.2,', label: '19.2%' },
                  { value: '22', label: '22%' },
                  { value: '24', label: '24%' },
                ]}
                onChange={(value: string): void => {
                  form.setFieldsValue({ interest: value });
                  setInterest(value);
                }}
                className="deposit__input"
              />
            </div>
          </Form.Item>
          {/* <Form.Item
            name="amount"
            rules={[{ required: true, message: 'Please enter amount' }]}
          > */}
          <div>
            <h4>Amount</h4>
            <Input
              disabled
              type="number"
              onWheel={(e) => {
                e.currentTarget.blur();
              }}
              value={amount}
              className="deposit__input"
              placeholder="Enter amount"
            />
          </div>
          {/* </Form.Item> */}
          <Form.Item
            name="offlineAmount"
            rules={[{ required: true, message: 'Please enter offline amount' }]}
          >
            <div>
              <h4>Offline amount</h4>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  console.log(form.getFieldValue('onlineAmount'));
                  setAmount(
                    Number(e.target.value) +
                      Number(form.getFieldValue('onlineAmount') ?? 0)
                  );
                }}
                className="deposit__input"
                placeholder="Enter offline amount"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="onlineAmount"
            rules={[{ required: true, message: 'Please enter offline amount' }]}
          >
            <div>
              <h4>Online amount</h4>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                onChange={(e) => {
                  setAmount(
                    Number(e.target.value) +
                      Number(form.getFieldValue('offlineAmount') ?? 0)
                  );
                }}
                className="deposit__input"
                placeholder="Enter online amount"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="cardNumber"
            rules={[{ required: true, message: 'Please enter card number' }]}
          >
            <div>
              <h4>Card Number</h4>
              <Input
                className="deposit__input"
                placeholder="Enter card number"
              />
            </div>
          </Form.Item>
          <Form.Item
            name="nominee"
            rules={[{ required: true, message: 'Please enter nominee' }]}
          >
            <div>
              <h4>Nominee</h4>
              <Input className="deposit__input" placeholder="Enter nominee" />
            </div>
          </Form.Item>
          <Form.Item
            name="investedDate"
            rules={[{ required: true, message: 'Please select invested date' }]}
          >
            <div>
              <h4>Invested Date</h4>
              <DatePicker
                value={datePickerDate}
                onChange={onChangeDate}
                className="deposit__input"
              />
            </div>
          </Form.Item>
        </div>

        <Button
          htmlType="submit"
          className="deposit__save__button"
          disabled={customerId === ''}
        >
          {isLoading ? (
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 24, color: 'white' }}
                  spin
                />
              }
            />
          ) : (
            <span>Add Deposit</span>
          )}
        </Button>
      </Form>

      <Modal
        width={800}
        title="Deposit Receipt"
        open={receiptModalOpen}
        onCancel={handleReceiptModalClose}
        destroyOnClose
        footer={() => (
          <Button onClick={handleReceiptModalClose}>Close</Button>
        )}
      >
        {receiptData !== null && (
          <DepositReceiptPrintable receipt={receiptData} />
        )}
      </Modal>
    </div>
  );
}
